package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.MeetingRoom
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.model.Room
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RoomManagementScreen(onNavigateBack: () -> Unit = {}) {
    val viewModel = koinViewModel<AdminRoomViewModel>()
    val rooms by viewModel.rooms.collectAsState()
    val roomTypes = viewModel.roomTypes

    var selectedFilter by remember { mutableStateOf("Tümü") }
    var showAddDialog by remember { mutableStateOf(false) }

    // Dialog state for edit/delete
    var selectedRoomForEdit by remember { mutableStateOf<Room?>(null) }
    var roomToDelete by remember { mutableStateOf<Room?>(null) }

    val filteredRooms = remember(rooms, selectedFilter) {
        if (selectedFilter == "Tümü") rooms else rooms.filter { it.roomType == selectedFilter }
    }

    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        viewModel.loadData()
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = onNavigateBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Oda Yönetimi"),
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "${rooms.size} ${l10n("Oda Tanımlı")}",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = { showAddDialog = true },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(6.dp))
                            Text(l10n("Yeni Oda"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }

                        LanguageSelectorRow(isDarkTheme = false)
                    }
                }
            }
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            // Filter Chips Bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                val allFilters = listOf("Tümü") + roomTypes
                allFilters.forEach { filter ->
                    val isSelected = selectedFilter == filter
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = if (isSelected) Color(0xFF0F172A) else Color.White,
                        border = BorderStroke(1.dp, if (isSelected) Color(0xFF0F172A) else Color(0xFFCBD5E1)),
                        modifier = Modifier.clickable { selectedFilter = filter }
                    ) {
                        Text(
                            text = if (filter == "Tümü") l10n("Tümü") else l10n(filter),
                            fontSize = 13.sp,
                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                            color = if (isSelected) Color.White else Color(0xFF475569),
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                        )
                    }
                }
            }

            // Room Cards List
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScrollbar(listState)
            ) {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    items(filteredRooms, key = { it.id ?: it.roomNumber }) { room ->
                        AdminRoomCard(
                            room = room,
                            onEdit = { selectedRoomForEdit = room },
                            onDelete = { roomToDelete = room }
                        )
                    }

                    item {
                        Spacer(Modifier.height(32.dp))
                    }
                }
            }
        }
    }

    // Add Room Dialog
    if (showAddDialog) {
        AddRoomDialog(
            roomTypes = roomTypes,
            onDismiss = { showAddDialog = false },
            onAdd = { number, type ->
                viewModel.addRoom(number, type)
                showAddDialog = false
            }
        )
    }

    // Edit Room Dialog
    if (selectedRoomForEdit != null) {
        val room = selectedRoomForEdit!!
        EditRoomDialog(
            room = room,
            roomTypes = roomTypes,
            onDismiss = { selectedRoomForEdit = null },
            onSave = { newType, newNumber, newStatus ->
                viewModel.updateRoom(room.id ?: "", newType, newNumber, newStatus)
                selectedRoomForEdit = null
            }
        )
    }

    // Delete Confirmation Dialog
    if (roomToDelete != null) {
        val room = roomToDelete!!
        AlertDialog(
            onDismissRequest = { roomToDelete = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text(l10n("Odayı Sil"), fontWeight = FontWeight.Bold) },
            text = { Text("${l10n("Oda")} ${room.roomNumber} ${l10n("kalıcı olarak silinecek. Onaylıyor musunuz?")}") },
            confirmButton = {
                Button(
                    onClick = {
                        viewModel.deleteRoom(room.id ?: "")
                        roomToDelete = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Text(l10n("Sil"), color = Color.White, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { roomToDelete = null }) {
                    Text(l10n("İptal"), color = Color(0xFF64748B))
                }
            }
        )
    }
}

@Composable
private fun AdminRoomCard(
    room: Room,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val statusColor = when (room.status) {
        "AVAILABLE", "Temiz" -> Color(0xFF10B981)
        "OCCUPIED", "Dolu" -> Color(0xFFEF4444)
        "CLEAN" -> Color(0xFF3B82F6)
        "MAINTENANCE", "Bakımda" -> Color(0xFFF59E0B)
        else -> Color(0xFF64748B)
    }

    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.weight(1f)
            ) {
                // Room Number Circle
                Box(
                    modifier = Modifier
                        .size(46.dp)
                        .clip(CircleShape)
                        .background(Color(0xFF0F172A)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = room.roomNumber,
                        color = Color.White,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp
                    )
                }

                Spacer(Modifier.width(14.dp))

                Column {
                    Text(
                        text = l10n(room.roomType),
                        fontSize = 15.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0F172A)
                    )
                    Spacer(Modifier.height(4.dp))
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(statusColor)
                        )
                        Text(
                            text = l10n(room.status),
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = statusColor
                        )
                    }
                }
            }

            // Action Buttons
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                IconButton(onClick = onEdit) {
                    Icon(
                        imageVector = Icons.Outlined.Edit,
                        contentDescription = l10n("Düzenle"),
                        tint = Color(0xFF2563EB),
                        modifier = Modifier.size(20.dp)
                    )
                }
                IconButton(onClick = onDelete) {
                    Icon(
                        imageVector = Icons.Outlined.Delete,
                        contentDescription = l10n("Sil"),
                        tint = Color(0xFFDC2626),
                        modifier = Modifier.size(20.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun AddRoomDialog(
    roomTypes: List<String>,
    onDismiss: () -> Unit,
    onAdd: (String, String) -> Unit
) {
    var roomNumber by remember { mutableStateOf("") }
    var selectedType by remember { mutableStateOf(roomTypes.firstOrNull() ?: "Standart") }
    var typeDropdownOpen by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.MeetingRoom, contentDescription = null, tint = Color(0xFF2563EB))
                Spacer(Modifier.width(8.dp))
                Text(l10n("Yeni Oda Ekle"), fontWeight = FontWeight.Bold, fontSize = 18.sp)
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                // Room Type Dropdown
                Box {
                    OutlinedTextField(
                        value = l10n(selectedType),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Oda Tipi")) },
                        trailingIcon = {
                            IconButton(onClick = { typeDropdownOpen = !typeDropdownOpen }) {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                            }
                        },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth().clickable { typeDropdownOpen = !typeDropdownOpen }
                    )
                    DropdownMenu(
                        expanded = typeDropdownOpen,
                        onDismissRequest = { typeDropdownOpen = false }
                    ) {
                        roomTypes.forEach { type ->
                            DropdownMenuItem(
                                text = { Text(l10n(type)) },
                                onClick = {
                                    selectedType = type
                                    typeDropdownOpen = false
                                }
                            )
                        }
                    }
                }

                OutlinedTextField(
                    value = roomNumber,
                    onValueChange = {
                        roomNumber = it
                        errorMessage = null
                    },
                    label = { Text(l10n("Oda Numarası (Örn: 101)")) },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )

                if (errorMessage != null) {
                    Text(errorMessage ?: "", color = Color(0xFFDC2626), fontSize = 12.sp)
                }
            }
        },
        confirmButton = {
            val emptyErrorText = l10n("Lütfen oda numarası giriniz.")
            Button(
                onClick = {
                    if (roomNumber.isBlank()) {
                        errorMessage = emptyErrorText
                        return@Button
                    }
                    onAdd(roomNumber.trim(), selectedType)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(l10n("Ekle"), fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(l10n("İptal"), color = Color(0xFF64748B))
            }
        }
    )
}

@Composable
private fun EditRoomDialog(
    room: Room,
    roomTypes: List<String>,
    onDismiss: () -> Unit,
    onSave: (String, String, String) -> Unit
) {
    var roomNumber by remember { mutableStateOf(room.roomNumber) }
    var selectedType by remember { mutableStateOf(room.roomType) }
    var selectedStatus by remember { mutableStateOf(room.status) }

    var typeDropdownOpen by remember { mutableStateOf(false) }
    var statusDropdownOpen by remember { mutableStateOf(false) }

    val availableStatuses = listOf("AVAILABLE", "OCCUPIED", "CLEAN", "MAINTENANCE")

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.Edit, contentDescription = null, tint = Color(0xFF2563EB))
                Spacer(Modifier.width(8.dp))
                Text(l10n("Odayı Düzenle"), fontWeight = FontWeight.Bold, fontSize = 18.sp)
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                // Room Number
                OutlinedTextField(
                    value = roomNumber,
                    onValueChange = { roomNumber = it },
                    label = { Text(l10n("Oda Numarası")) },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                )

                // Room Type
                Box {
                    OutlinedTextField(
                        value = l10n(selectedType),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Oda Tipi")) },
                        trailingIcon = {
                            IconButton(onClick = { typeDropdownOpen = !typeDropdownOpen }) {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                            }
                        },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth().clickable { typeDropdownOpen = !typeDropdownOpen }
                    )
                    DropdownMenu(
                        expanded = typeDropdownOpen,
                        onDismissRequest = { typeDropdownOpen = false }
                    ) {
                        roomTypes.forEach { type ->
                            DropdownMenuItem(
                                text = { Text(l10n(type)) },
                                onClick = {
                                    selectedType = type
                                    typeDropdownOpen = false
                                }
                            )
                        }
                    }
                }

                // Room Status
                Box {
                    OutlinedTextField(
                        value = l10n(selectedStatus),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Oda Durumu")) },
                        trailingIcon = {
                            IconButton(onClick = { statusDropdownOpen = !statusDropdownOpen }) {
                                Icon(Icons.Default.ArrowDropDown, contentDescription = null)
                            }
                        },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth().clickable { statusDropdownOpen = !statusDropdownOpen }
                    )
                    DropdownMenu(
                        expanded = statusDropdownOpen,
                        onDismissRequest = { statusDropdownOpen = false }
                    ) {
                        availableStatuses.forEach { status ->
                            DropdownMenuItem(
                                text = { Text(l10n(status)) },
                                onClick = {
                                    selectedStatus = status
                                    statusDropdownOpen = false
                                }
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    onSave(selectedType, roomNumber.trim(), selectedStatus)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(l10n("Kaydet"), fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(l10n("İptal"), color = Color(0xFF64748B))
            }
        }
    )
}
